import { useParams, Link, Navigate } from "react-router-dom";
import { PageHero } from "@/components/PageHero";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal } from "@/components/Reveal";
import { CTASection } from "@/components/CTASection";
import { getCity, SERVICES, CITIES, comboPath } from "@/data/seoPages";
import { IMAGES } from "@/data/site";
import { MapPin } from "@phosphor-icons/react";
import { useSEO, serviceLd, breadcrumbLd, faqLd, localBusinessLd } from "@/lib/seo";

export default function CityLanding() {
  const { slug } = useParams();
  const city = getCity(slug);
  const constructionLine = city?.core
    ? `custom homes, renovations and commercial construction`
    : `select construction projects`;

  const faqs = city
    ? [
        { q: `Do you serve ${city.name}?`, a: city.core
            ? `Yes — ${city.name} is part of our core service area for both construction and digital services.`
            : `Yes. We offer branding, web design, marketing and AI across ${city.name} and all of Canada, and take select construction projects in the ${city.province} area.` },
        { q: `What can MRE do for ${city.name} businesses?`, a: `We build the physical space your business lives in and the digital presence that grows it — one partner for the structure and the signal.` },
        { q: `How do I get a quote in ${city.name}?`, a: `Book a free consultation. We'll scope your project and send a transparent quote — no jargon, no surprises.` },
      ]
    : [];

  useSEO({
    title: city ? `Builder & Digital Agency in ${city.name} | MRE — Free Quote` : "Locations | MRE Developments",
    description: city
      ? `MRE Developments in ${city.name}, ${city.code}: ${constructionLine} plus branding, web design, marketing and AI. One partner to build and grow. Get a free quote.`
      : "",
    path: city ? `/locations/${city.slug}` : "/locations",
    image: IMAGES.heroFallback,
    jsonLd: city
      ? [
          localBusinessLd(),
          serviceLd(`Construction & Digital Services in ${city.name}`, `Build and grow services for ${city.name}, ${city.province}.`),
          faqLd(faqs),
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Locations", path: "/locations" },
            { name: city.name, path: `/locations/${city.slug}` },
          ]),
        ]
      : [],
  });

  if (!city) return <Navigate to="/locations" replace />;

  const buildServices = SERVICES.filter((s) => s.group !== "Digital & AI");
  const digitalServices = SERVICES.filter((s) => s.group === "Digital & AI");
  const nearby = CITIES.filter((c) => c.code === city.code && c.slug !== city.slug).slice(0, 6);

  return (
    <div data-testid="city-page">
      <PageHero
        overline={`Locations · ${city.province}`}
        title={`Build & Grow in ${city.name}`}
        intro={`One partner for ${city.name}: ${constructionLine}, plus the branding, websites, marketing and AI that make ${city.name} businesses grow.`}
        image={IMAGES.heroFallback}
        breadcrumb={[
          { name: "Home", path: "/" },
          { name: "Locations", path: "/locations" },
          { name: city.name, path: `/locations/${city.slug}` },
        ]}
      />

      {/* Construction services in city */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 lg:py-24">
        <SectionHeading overline="Construction & trades" title={`Construction in ${city.name}`} intro={city.core ? `Full-service building and trades across ${city.name}.` : `Select construction projects in the ${city.province} area.`} />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border mt-10">
          {buildServices.map((s) => (
            <Link key={s.slug} to={comboPath(s.slug, city.slug)} className="bg-background p-6 hover:bg-secondary/40 transition-colors group" data-testid={`city-service-${s.slug}`}>
              <span className="font-mono-tech text-[10px] tracking-widest uppercase text-primary">{s.group}</span>
              <h3 className="font-display uppercase tracking-tight text-lg mt-1 group-hover:text-primary transition-colors">{s.name} in {city.name}</h3>
            </Link>
          ))}
        </div>
      </section>

      {/* Digital services */}
      <section className="border-y border-border bg-secondary/20">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 lg:py-24">
          <SectionHeading overline="Digital & AI · Canada-wide" title={`Grow your ${city.name} business`} intro="Branding, web, marketing and AI — available across Canada, wherever your customers are." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border mt-10">
            {digitalServices.map((s) => (
              <Link key={s.slug} to={comboPath(s.slug, city.slug)} className="bg-background p-6 hover:bg-secondary/40 transition-colors group" data-testid={`city-digital-${s.slug}`}>
                <span className="font-mono-tech text-[10px] tracking-widest uppercase text-primary">{s.group}</span>
                <h3 className="font-display uppercase tracking-tight text-lg mt-1 group-hover:text-primary transition-colors">{s.name}</h3>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 lg:py-24 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <SectionHeading overline="FAQ" title={`${city.name} questions`} />
        </div>
        <div className="lg:col-span-8 border-t border-border">
          {faqs.map((f, i) => (
            <Reveal key={i} className="border-b border-border py-6">
              <h3 className="font-display uppercase tracking-tight text-lg">{f.q}</h3>
              <p className="mt-2 text-muted-foreground leading-relaxed max-w-2xl">{f.a}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Nearby */}
      {nearby.length > 0 && (
        <section className="border-t border-border">
          <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-16">
            <SectionHeading overline="Nearby" title={`Other ${city.province} locations`} />
            <div className="flex flex-wrap gap-3 mt-8">
              {nearby.map((c) => (
                <Link key={c.slug} to={`/locations/${c.slug}`} data-testid={`nearby-${c.slug}`} className="font-mono-tech text-xs tracking-widest uppercase border border-border px-4 h-10 inline-flex items-center gap-2 hover:border-primary hover:text-primary transition-colors">
                  <MapPin size={14} /> {c.name}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <CTASection title={`Let's build & grow in ${city.name}.`} />
    </div>
  );
}
